import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { Field as AkField } from '@atlaskit/form';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { HOLIDAY_PREFIX } from '../../../../../../../common/ui/constants.tsx';
import { formatDate } from '../../../../../../../common/utils/calendars/utils';
import { Edit, type EditProps } from './edit';
import messages from './messages';
import { Remove, type RemoveProps } from './remove';

type Props = {
	isRecurring: boolean;
	fieldId: number;
	defaultDate: string;
	defaultName: string;
} & EditProps &
	RemoveProps;

type ViewProps = {
	isRecurring: boolean;
	value: string;
	defaultName: string;
	fieldId: number;
} & EditProps &
	RemoveProps;

const ViewRow = ({
	isRecurring,
	value,
	defaultName,
	onEditRequested,
	onRemove,
	fieldId,
	intl: { formatMessage },
}: ViewProps) => {
	const locale = useLocale();
	const everyText = formatMessage(messages.every);
	const formattedDate = useMemo(
		() =>
			formatDate({
				iso8601Date: value,
				locale,
				isRecurring,
				message: everyText,
			}),
		[everyText, isRecurring, locale, value],
	);
	return (
		<Row>
			<Label
				data-test-id={`servicedesk-sla-settings.ui.calendars.editable.fields.holidays.holiday-${fieldId}.read-only-view-with-actions.name`}
			>
				{defaultName}
			</Label>
			<Label
				data-test-id={`servicedesk-sla-settings.ui.calendars.editable.fields.holidays.holiday-${fieldId}.read-only-view-with-actions.date`}
			>
				{formattedDate}
			</Label>
			<Label>{isRecurring ? formatMessage(messages.yearly) : formatMessage(messages.never)}</Label>
			<Edit onEditRequested={onEditRequested} />
			<Remove onRemove={onRemove} />
		</Row>
	);
};

const IntlViewRow = injectIntl(ViewRow);

const ViewWithActions = ({
	fieldId,
	isRecurring,
	defaultDate,
	defaultName,
	onEditRequested,
	onRemove,
}: Props) => (
	<AkField
		name={`${HOLIDAY_PREFIX}${fieldId}`}
		defaultValue={{
			defaultName,
			defaultDate,
			isRecurring,
		}}
	>
		{() => (
			<IntlViewRow
				isRecurring={isRecurring}
				defaultName={defaultName}
				value={defaultDate}
				onEditRequested={onEditRequested}
				onRemove={onRemove}
				fieldId={fieldId}
			/>
		)}
	</AkField>
);

export const ReadOnlyViewWithActions = injectIntl(ViewWithActions);

// To do use replace with flex or use flex fallback for Ie11

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Row = styled.div({
	display: 'grid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	gridGap: `${gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	gridTemplateColumns: `1fr 1fr 1fr ${gridSize * 4}px ${gridSize * 4}px`,
	justifyItems: 'start',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Label = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.DN0),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	width: '100%',
});
